import { useEffect, useMemo, useState } from "react";
import { pickDisplayLabel, pickEntityId } from "./buildJobJourneySteps.js";
import { resolveJobJourneyLinks } from "./resolveJobJourneyLinks.js";

/**
 * Resolve Customer → Lead → Quote → Invoice → Order links + display labels.
 *
 * @param {{
 *   current: 'customer'|'lead'|'quote'|'invoice'|'order',
 *   customerId?: string|null,
 *   leadId?: string|null,
 *   quoteId?: string|null,
 *   invoiceId?: string|null,
 *   orderId?: string|null,
 *   customerDisplay?: string|null,
 *   leadDisplay?: string|null,
 *   quoteDisplay?: string|null,
 *   invoiceDisplay?: string|null,
 *   orderDisplay?: string|null,
 *   enabled?: boolean,
 * }} seed
 */
export function useJobJourneyLinks(seed) {
  const enabled = seed?.enabled !== false;
  const current = seed?.current;

  const customerId = pickEntityId(seed?.customerId);
  const leadId = pickEntityId(seed?.leadId);
  const quoteId = pickEntityId(seed?.quoteId);
  const invoiceId = pickEntityId(seed?.invoiceId);
  const orderId = pickEntityId(seed?.orderId);
  const customerDisplay = pickDisplayLabel(seed?.customerDisplay);
  const leadDisplay = pickDisplayLabel(seed?.leadDisplay);
  const quoteDisplay = pickDisplayLabel(seed?.quoteDisplay);
  const invoiceDisplay = pickDisplayLabel(seed?.invoiceDisplay);
  const orderDisplay = pickDisplayLabel(seed?.orderDisplay);

  const seedKey = useMemo(
    () =>
      JSON.stringify({
        current,
        customerId,
        leadId,
        quoteId,
        invoiceId,
        orderId,
        customerDisplay,
        leadDisplay,
        quoteDisplay,
        invoiceDisplay,
        orderDisplay,
        enabled,
      }),
    [
      current,
      customerId,
      leadId,
      quoteId,
      invoiceId,
      orderId,
      customerDisplay,
      leadDisplay,
      quoteDisplay,
      invoiceDisplay,
      orderDisplay,
      enabled,
    ]
  );

  const initial = useMemo(
    () => ({
      current,
      customerId,
      leadId,
      quoteId,
      invoiceId,
      orderId,
      customerDisplay,
      leadDisplay,
      quoteDisplay,
      invoiceDisplay,
      orderDisplay,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by seedKey
    [seedKey]
  );

  const [links, setLinks] = useState(initial);

  useEffect(() => {
    setLinks(initial);

    if (!enabled || !current) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const resolved = await resolveJobJourneyLinks(initial);
        if (!cancelled) setLinks(resolved);
      } catch {
        if (!cancelled) setLinks(initial);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, current, initial]);

  return links;
}

export default useJobJourneyLinks;
